import React, { useEffect, useState } from 'react'
import { Col, Row, Form as RBForm } from 'react-bootstrap'
import { Form, Formik } from 'formik'
import { ShimmerButton, ShimmerText, ShimmerTitle } from 'react-shimmer-effects'
import { useParams } from 'react-router-dom'
import { object, string } from 'yup'
import {
  Button,
  useUserService,
  useErrorService,
  useNotificationService,
} from '@abroad/components'
import { InputField } from '../form'
import API from '../../utils/API'
import { gtmContinueExperienceBillingInfoAction } from './gtmfunctions'

const validationSchema = object().shape({
  line1: string().notRequired(),
  line2: string().notRequired(),
  state: string().notRequired(),
  country: string().notRequired(),
  city: string().notRequired(),
  postal_code: string().notRequired(),
  billerName: string().required('Biller Name is required'),
})

const QuestUserInfoForm = ({
  onBack = () => {},
  paymentType,
  selectedPaymentMethod,
  finalAmountToPay,
  makePayment = () => {},
  canPayRemaining,
  eurRate,
  billingAddress,
  setBillingAddress = () => {},
}) => {
  const [isLoading, setIsLoading] = useState(true)
  // const [billingAddress, setBillingAddress] = useState(defaultAddress)
  const [isPaying, setIsPaying] = useState(false)
  const { user } = useUserService()
  const notification = useNotificationService()
  const Error = useErrorService()
  const userId = user?._id
  const { questId } = useParams()

  useEffect(() => {
    const getUserAddress = async () => {
      setIsLoading(true)
      try {
        const { data } = await API.user.getUserAddress(userId)
        if (data?.address) {
          // Remove any fields that are null or ''
          Object.keys(data?.address).forEach((key) => {
            if (data?.address[key] === null || data?.address[key] === '') {
              delete data?.address[key]
            }
          })

          const updatedAddress = {
            ...data?.address,
            billerName: data?.name || '',
          }

          setBillingAddress((prev) => ({
            ...prev,
            ...updatedAddress,
          }))
          setIsLoading(false)
        } else {
          setIsLoading(false)
        }
      } catch (e) {
        setIsLoading(false)
        Error.showError(e)
      }
    }
    getUserAddress()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (isLoading) {
    return (
      <div className='payment-form-shimmer mt-4'>
        {[1, 2, 3, 4, 5, 6].map((number, index) => (
          <React.Fragment key={index}>
            <ShimmerText line={1} />
            <ShimmerTitle
              line={1}
              variant='primary'
              className='input-shimmer mb-4'
              key={index}
            />
          </React.Fragment>
        ))}
        <ShimmerButton size='md' />
      </div>
    )
  }

  const onSubmit = async (values) => {
    try {
      gtmContinueExperienceBillingInfoAction(questId, userId)
      setIsPaying(true)
      const requestBody = {
        paymentType: canPayRemaining ? 'remaining' : paymentType,
        paymentMethod: selectedPaymentMethod,
        billerName: values.billerName,
        address: {
          line1: values.line1,
          line2: values.line2,
          state: values.state,
          country: values.country,
          city: values.city,
          postal_code: values.postal_code,
        },
      }
      const { data } = await API.quests.makePayment(questId, requestBody)
      makePayment(data, values)
      setIsPaying(false)
    } catch (error) {
      setIsPaying(false)
      if (error?.code === 'amount_too_large') {
        notification.showNotification(
          'SEPA payment limit is €10,000. Please use a different method',
          'danger',
        )
        return
      }
      notification.showNotification('Something went wrong! Please try again.')
      return null
    }
  }

  return (
    <Formik
      initialValues={billingAddress}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize={true}>
      {({ handleSubmit }) => (
        <RBForm
          onSubmit={handleSubmit}
          as={Form}
          className='my-info px-0 mt-30px'>
          <div className='add-card'>
            <div className='form-group credit-card-wrp'>
              <InputField
                label='FullName'
                name='billerName'
                type='text'
                size='lg'
                sm='10'
              />
            </div>
            <div className='form-group credit-card-wrp'>
              <InputField
                label='AddressLine1'
                name='line1'
                type='text'
                size='lg'
                sm='10'
              />
            </div>
            <div className='form-group credit-card-wrp'>
              <InputField
                label='AddressLine2'
                name='line2'
                type='text'
                size='lg'
                sm='10'
              />
            </div>
            <Row className='justify-content-between'>
              <Col className='col-6'>
                <InputField
                  label='Country'
                  name='country'
                  type='text'
                  size='lg'
                  sm='10'
                />
              </Col>
              <Col className='col-5'>
                <InputField
                  label='City'
                  name='city'
                  type='text'
                  size='lg'
                  sm='10'
                />
              </Col>
            </Row>
            <Row className='justify-content-between'>
              <Col className='col-6'>
                <InputField
                  label='QuestState'
                  name='state'
                  type='text'
                  size='lg'
                  sm='10'
                />
              </Col>
              <Col className='col-5'>
                <InputField
                  label='QuestPostalCode'
                  name='postal_code'
                  type='text'
                  size='lg'
                  sm='10'
                />
              </Col>
            </Row>
            <div className='d-flex justify-content-between'>
              <span className='s6 align-self-center'>Total Amount</span>
              <span className='s6'>
                {selectedPaymentMethod === 'sepa' && eurRate
                  ? `$ ${finalAmountToPay.toLocaleString()} USD | € ${(
                      finalAmountToPay * eurRate
                    ).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}`
                  : `$ ${finalAmountToPay.toLocaleString()} USD`}
              </span>
            </div>
            <Row className='d-flex justify-content-between'>
              <Col xl={4} lg={4} md={4} sm={4} xs={4}>
                <Button
                  className='text-uppercase payment-back-btn line-height-normal w-100 btn-fixed-height s9 mt-24px'
                  onClick={onBack}
                  disabled={isPaying}>
                  Back
                </Button>
              </Col>
              <Col xl={8} lg={8} md={8} sm={8} xs={8}>
                <Button
                  type='submit'
                  variant='saffron'
                  className='border-radius-normal border-none w-100 mt-24px btn-fixed-height'
                  isLoading={isPaying}
                  size='md'>
                  Continue
                </Button>
              </Col>
            </Row>
          </div>
        </RBForm>
      )}
    </Formik>
  )
}

export default QuestUserInfoForm
